import { useState } from "react";
import { X, ShoppingCart, Trash2, Copy, Check } from "lucide-react";
import { api, fileUrl, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useCart } from "@/hooks/useCart";

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

// Payment method rendering shared between drawer + confirmation
function PaymentHandleList({ handles, total }) {
  const [copied, setCopied] = useState("");
  const copy = (label, val) => {
    try { navigator.clipboard.writeText(val); setCopied(label); toast.success("Copied"); setTimeout(() => setCopied(""), 1500); } catch {}
  };
  const rows = [];
  if (handles?.venmo) rows.push({ label: "Venmo", val: `@${handles.venmo.replace(/^@/, "")}`, bg: "#EAF3FE", fg: "#1D5FBF" });
  if (handles?.cashapp) rows.push({ label: "CashApp", val: `$${handles.cashapp.replace(/^\$/, "")}`, bg: "#E7F7EA", fg: "#177E3B" });
  if (handles?.zelle) rows.push({ label: "Zelle", val: handles.zelle, bg: "#F1EBFF", fg: "#5B21B6" });
  if (handles?.paypal) rows.push({ label: "PayPal.me", val: handles.paypal.replace(/^https?:\/\/(www\.)?paypal\.me\//, "").replace(/^@/, ""), bg: "#EFF6FF", fg: "#003087" });
  if (rows.length === 0) return (
    <div className="p-4 rounded-2xl bg-[#FFF9EF] border border-[#EBDCD1] text-sm text-[#857163]" data-testid="checkout-no-payment-methods">
      The seller hasn't set up payment methods yet — they'll follow up with instructions.
    </div>
  );
  return (
    <div className="space-y-2" data-testid="checkout-payment-methods">
      <div className="text-xs uppercase tracking-widest text-[#857163] mb-1">Send {money(total)} via</div>
      {rows.map((r) => (
        <div key={r.label} className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-2xl font-semibold text-sm" style={{ background: r.bg, color: r.fg }} data-testid={`pay-row-${r.label.toLowerCase()}`}>
          <div className="min-w-0"><div className="text-[10px] uppercase tracking-widest opacity-80">{r.label}</div><div className="truncate">{r.val}</div></div>
          <button onClick={() => copy(r.label, r.val)} className="shrink-0 p-1.5 rounded-full bg-white/70 hover:bg-white transition" data-testid={`pay-copy-${r.label.toLowerCase()}`}>
            {copied === r.label ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      ))}
      {handles?.notes && (
        <div className="text-xs text-[#857163] italic mt-2 whitespace-pre-wrap" data-testid="pay-notes">{handles.notes}</div>
      )}
    </div>
  );
}


export function CartDrawer({ username, businessName, accent = "#FF7E67", open, onClose }) {
  const cart = useCart(username);
  const [checkoutName, setCheckoutName] = useState("");
  const [checkoutEmail, setCheckoutEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmedOrder, setConfirmedOrder] = useState(null);
  const [stage, setStage] = useState("cart"); // cart → checkout → done

  const close = () => {
    onClose?.();
    // Reset stage after close so re-open starts fresh
    setTimeout(() => {
      setStage("cart");
      setConfirmedOrder(null);
      setCheckoutName("");
      setCheckoutEmail("");
      setNote("");
    }, 300);
  };

  const submit = async () => {
    if (!checkoutName.trim()) { toast.error("What's your name?"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(checkoutEmail.trim().toLowerCase())) { toast.error("Enter a valid email"); return; }
    if (cart.items.length === 0) { toast.error("Cart is empty"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/shop/${username}/checkout`, {
        customer_name: checkoutName.trim(),
        customer_email: checkoutEmail.trim().toLowerCase(),
        customer_note: note.trim() || null,
        items: cart.items.map((it) => ({ product_id: it.product_id, quantity: it.quantity })),
      });
      setConfirmedOrder(data);
      cart.clear();
      setStage("done");
      toast.success("Order placed! Check your email for payment instructions.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex justify-end bg-black/40" onClick={close} data-testid="cart-drawer-backdrop">
      <div
        className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="cart-drawer"
      >
        <div className="p-4 border-b border-[#EBDCD1] flex items-center justify-between shrink-0" style={{ background: accent, color: "#fff" }}>
          <div className="font-heading text-lg flex items-center gap-2">
            <ShoppingCart className="w-5 h-5" />
            {stage === "done" ? "Order placed" : `Your cart · ${businessName || "@" + username}`}
          </div>
          <button onClick={close} className="p-1 hover:bg-white/10 rounded-full" data-testid="cart-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {stage === "cart" && (
            cart.items.length === 0 ? (
              <div className="text-center py-16 text-[#857163]" data-testid="cart-empty">
                <ShoppingCart className="w-10 h-10 mx-auto text-[#EBDCD1] mb-2" />
                <div className="font-semibold text-[#3B2C24]">Your cart is empty</div>
                <div className="text-xs mt-1">Add products from this shop to get started.</div>
              </div>
            ) : (
              <div className="space-y-2">
                {cart.items.map((it) => (
                  <div key={it.product_id} className="flex gap-3 p-2 rounded-2xl border border-[#EBDCD1]" data-testid={`cart-item-${it.product_id}`}>
                    <div className="w-16 h-16 rounded-xl overflow-hidden bg-[#F9EFE6] shrink-0">
                      {it.image_url ? (
                        <img src={fileUrl(it.image_url)} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full grid place-items-center text-[10px] text-[#857163]">No image</div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-sm text-[#3B2C24] truncate">{it.title}</div>
                      <div className="text-xs text-[#857163]">{money(it.unit_price_cents)} each</div>
                      <div className="mt-1.5 flex items-center gap-1">
                        <button onClick={() => cart.setQuantity(it.product_id, it.quantity - 1)} className="w-6 h-6 rounded-full bg-white border border-[#EBDCD1] text-[#3B2C24] text-sm" data-testid={`cart-qty-dec-${it.product_id}`}>–</button>
                        <input
                          type="number" min="1" max="99"
                          value={it.quantity}
                          onChange={(e) => cart.setQuantity(it.product_id, e.target.value)}
                          className="w-12 text-center text-sm rounded-full border border-[#EBDCD1] py-0.5"
                          data-testid={`cart-qty-input-${it.product_id}`}
                        />
                        <button onClick={() => cart.setQuantity(it.product_id, it.quantity + 1)} className="w-6 h-6 rounded-full bg-white border border-[#EBDCD1] text-[#3B2C24] text-sm" data-testid={`cart-qty-inc-${it.product_id}`}>+</button>
                        <button onClick={() => cart.removeItem(it.product_id)} className="ml-auto text-[#C2185B] p-1" data-testid={`cart-remove-${it.product_id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                    <div className="text-right shrink-0 min-w-[60px]">
                      <div className="font-semibold text-[#3B2C24]">{money(it.unit_price_cents * it.quantity)}</div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {stage === "checkout" && (
            <div className="space-y-3" data-testid="checkout-form">
              <div className="text-xs uppercase tracking-widest text-[#857163]">Contact info</div>
              <label className="block">
                <span className="text-xs text-[#857163]">Your name</span>
                <input value={checkoutName} onChange={(e) => setCheckoutName(e.target.value)} className="cp-input mt-1" maxLength={80} data-testid="checkout-name" />
              </label>
              <label className="block">
                <span className="text-xs text-[#857163]">Your email</span>
                <input type="email" value={checkoutEmail} onChange={(e) => setCheckoutEmail(e.target.value)} className="cp-input mt-1" data-testid="checkout-email" />
              </label>
              <label className="block">
                <span className="text-xs text-[#857163]">Note to seller <span className="text-[#B79885]">(optional)</span></span>
                <textarea value={note} onChange={(e) => setNote(e.target.value.slice(0, 1000))} rows={3} className="cp-input mt-1" placeholder="Pickup preference, allergies, etc." data-testid="checkout-note" />
              </label>
              <div className="mt-2 p-3 rounded-2xl bg-[#FFF9EF] border border-[#EBDCD1] text-xs text-[#857163]">
                <b className="text-[#3B2C24]">Heads up:</b> Payment happens outside the app. After you place the order, you'll see the seller's Venmo/CashApp/Zelle handles + we'll email them to you.
              </div>
            </div>
          )}

          {stage === "done" && confirmedOrder && (
            <div className="space-y-4" data-testid="checkout-confirmation">
              <div className="text-center">
                <div className="w-14 h-14 rounded-full mx-auto grid place-items-center" style={{ background: `${accent}22`, color: accent }}>
                  <Check className="w-7 h-7" />
                </div>
                <div className="font-heading text-xl text-[#3B2C24] mt-2">Order placed 🎉</div>
                <div className="text-xs text-[#857163] mt-1">
                  Order <b>#{confirmedOrder.id.slice(-8).toUpperCase()}</b> — the seller has been notified.
                </div>
              </div>

              <div className="p-3 rounded-2xl bg-[#F9EFE6]/40 border border-[#EBDCD1]">
                {confirmedOrder.items.map((it) => (
                  <div key={it.product_id} className="flex justify-between py-1 text-sm">
                    <span className="text-[#3B2C24]">{it.title} <span className="text-[#857163]">× {it.quantity}</span></span>
                    <span className="font-semibold text-[#3B2C24]">{money(it.subtotal_cents)}</span>
                  </div>
                ))}
                <div className="border-t border-[#EBDCD1] mt-2 pt-2 flex justify-between text-sm">
                  <b>Total</b><b style={{ color: accent }}>{money(confirmedOrder.total_cents)}</b>
                </div>
              </div>

              <PaymentHandleList handles={confirmedOrder.payment_handles} total={confirmedOrder.total_cents} />

              <div className="text-center text-xs text-[#857163]">
                A copy of the order + payment methods was emailed to <b>{confirmedOrder.customer_email}</b>.
              </div>
            </div>
          )}
        </div>

        {stage !== "done" && cart.items.length > 0 && (
          <div className="border-t border-[#EBDCD1] p-4 shrink-0 bg-white" data-testid="cart-footer">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-[#857163]">{cart.itemCount} item{cart.itemCount === 1 ? "" : "s"}</span>
              <span className="font-heading text-xl" style={{ color: accent }} data-testid="cart-subtotal">{money(cart.subtotalCents)}</span>
            </div>
            {stage === "cart" ? (
              <button
                onClick={() => setStage("checkout")}
                className="w-full py-3 rounded-full font-semibold text-white text-sm hover:opacity-90"
                style={{ background: accent }}
                data-testid="cart-checkout-btn"
              >Checkout →</button>
            ) : (
              <div className="flex gap-2">
                <button onClick={() => setStage("cart")} disabled={submitting} className="px-4 py-2.5 rounded-full text-sm border border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6] disabled:opacity-50" data-testid="checkout-back">← Back</button>
                <button
                  onClick={submit}
                  disabled={submitting}
                  className="flex-1 py-2.5 rounded-full font-semibold text-white text-sm hover:opacity-90 disabled:opacity-60"
                  style={{ background: accent }}
                  data-testid="checkout-submit"
                >
                  {submitting ? "Placing order…" : `Place order · ${money(cart.subtotalCents)}`}
                </button>
              </div>
            )}
          </div>
        )}

        {stage === "done" && (
          <div className="border-t border-[#EBDCD1] p-4 shrink-0 bg-white">
            <button onClick={close} className="w-full py-2.5 rounded-full font-semibold text-white text-sm" style={{ background: accent }} data-testid="checkout-close-done">Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
